import { Button, ColorTypes, actions } from "@omegagrid/core";
import { CellModel, ColumnSource, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, formatDateTime } from "@reporting/rp-app-host";
import { VectorReindexResult, VectorStatus } from "../types";

/**
 * Read-only overview of the semantic-search index of one wiki (GET wiki/vectors): whether the vector
 * API is configured, whether a saved page is indexed right away (the application documentation only —
 * a customer's wiki is indexed by the reindex alone) and the page / chunk counts, followed by the result of the last reindex run from
 * this view. The toolbar carries the two reindex actions (POST wiki/vectors/reindex): incremental =
 * pages without vectors plus a sweep of stale rows, full = every page again (after a model or
 * chunking change). The server enforces the rights (WikiWrite, SA for the application documentation).
 */
export class VectorsGridEditorAdapter extends GridEditorAdapter {

	private lastResult: VectorReindexResult = null;
	private lastRun: Date = null;
	private running = false;

	/** @param a 0 = the application documentation (the wiki accounting), otherwise the customer's own wiki */
	constructor(private a: number) { super() }

	getColumns(): ColumnSource[] {
		return [{
			data: 'property',
			header: msg('Property'),
			t: 't',
			e: false,
			w: 240,
		}, {
			data: 'value',
			header: msg('Value'),
			t: 't',
			e: false,
			w: 560,
		}];
	}

	async getRows(): Promise<RowSource[]> {
		const status = await rp.ms('wiki').get<VectorStatus>('vectors', { accounting: this.a });
		const rows: RowSource[] = [
			this.row(`API ${msg('configured')}`, status.configured ? msg('Yes') : msg('No')),
			this.row(msg('Indexed on save'), status.indexOnSave ? msg('Yes') : msg('No')),
		];
		if (status.error) {
			rows.push(this.row(msg('Error'), status.error));
		} else {
			rows.push(
				this.row(msg('Pages'), status.pages),
				this.row(msg('Indexed'), status.indexed),
				this.row(msg('Chunks'), status.chunks),
				this.row(msg('Stale rows'), status.stale),
			);
		}

		if (this.lastResult) {
			const r = this.lastResult;
			rows.push(
				this.row(msg('Last reindex'), formatDateTime(this.lastRun)),
				this.row(msg('Reindexed'), `${r.indexed} / ${r.pages}`),
				this.row(msg('New chunks'), r.chunks),
				this.row(msg('Deleted rows'), r.deleted),
			);
			if (r.aborted) {
				rows.push(this.row(msg('Aborted'), r.aborted), this.row(msg('Remaining'), r.remaining));
			}
			for (const failed of r.failed ?? []) {
				rows.push(this.row(`${msg('Failed page')}: ${failed.name}`, failed.error));
			}
		}
		return rows;
	}

	private row(property: string, value: unknown): RowSource {
		return { data: { property, value: value === null || value === undefined ? '—' : String(value) } };
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: false,
			filters: false,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
			},
			customToolbar: [{
				type: 'button',
				id: 'btnReindex',
				text: msg('Reindex'),
				icon: 'retweet',
				color: ColorTypes.Blue,
			}, {
				type: 'button',
				id: 'btnReindexFull',
				text: msg('Full reindex'),
				icon: 'retweet',
				color: ColorTypes.Accent,
			}] as ToolbarItem[],
		});
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent = null) {
		if (action !== 'btnReindex' && action !== 'btnReindexFull') return;
		if (this.running) return;
		await this.reindex(action === 'btnReindexFull');
	}

	private async reindex(full: boolean) {
		this.running = true;
		this.setButtonsDisabled(true);
		this.editor?.showLoader();
		const title = msg('Vector store');
		try {
			// a long request for a large wiki: the buttons stay disabled until it returns
			const response = await rp.ms('wiki').postRaw('vectors/reindex', {}, { accounting: this.a, full: full ? 1 : 0 });
			let body: Record<string, unknown> = null;
			try { body = await response.json(); } catch { body = null; }

			if (!response.ok) {
				// 409 = the vector API is not configured, 502 = it cannot be reached; the server says which
				rp.host.showAlert({ type: 'danger', title, content: (body?.message as string) ?? `HTTP ${response.status}` });
				return;
			}

			this.lastResult = body as unknown as VectorReindexResult;
			this.lastRun = new Date();
			const r = this.lastResult;
			if (r.aborted) {
				rp.host.showAlert({ type: 'danger', title, content: `${msg('Reindex stopped, the vector API is unavailable')}: ${r.aborted}` });
			} else if (r.failed?.length) {
				rp.host.showAlert({ type: 'warning', title, content: msg('Reindexed {indexed} of {pages} pages, {failed} failed', { data: { indexed: r.indexed, pages: r.pages, failed: r.failed.length } }) });
			} else {
				rp.host.showAlert({ type: 'info', title, content: msg('Reindexed {indexed} of {pages} pages ({chunks} chunks)', { data: { indexed: r.indexed, pages: r.pages, chunks: r.chunks } }) });
			}
		} catch (e) {
			rp.host.showAlert({ type: 'danger', title, content: (e as Error)?.message ?? String(e) });
		} finally {
			this.running = false;
			this.setButtonsDisabled(false);
			this.editor?.refresh();
		}
	}

	private setButtonsDisabled(disabled: boolean) {
		for (const id of ['btnReindex', 'btnReindexFull']) {
			const button = this.editor?.customToolbar?.controls?.get(id) as Button;
			if (button) button.disabled = disabled;
		}
	}

}
